import { X, Copy, ShareNetwork, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLang } from "@/contexts/LangContext";
import { HIGHLIGHT_COLORS } from "@/lib/highlightColors";

/**
 * SelectionActionBar — sticky 2-row bar docked above the bottom nav
 * whenever the user has one or more rows selected. Strict 2-row layout
 * (no separate header row) for a low-profile silhouette:
 *
 *   Row 1 (header + actions): Close ✕ and the "N seleccionados" count
 *                              on the left; icon-only Copy + Share on
 *                              the right.
 *   Row 2 (highlights):       five circular pastel swatches that tag
 *                              the selection as a favourite with that
 *                              colour.
 *
 * The five colours come from `/lib/highlightColors.js` and match the
 * product spec. Tapping a swatch:
 *   • saves the selection as favourites with `color_id = <id>`, OR
 *   • updates the colour of already-saved rows (single PATCH per row),
 *     OR
 *   • toggles them off if every selected row already has that exact
 *     colour (a "second tap of the same colour deselects" behaviour).
 *
 * The caller wires up two callbacks:
 *   • `applyHighlight(keys, colorId)` — async; persists the colour. Must
 *     return a user-facing message for the success toast.
 *   • `getRowColor(key)`              — sync; returns the saved colour
 *     id for a row, or null when not yet saved. Used to decide whether
 *     the same-colour tap should toggle off.
 */
export default function SelectionActionBar({
    selectedKeys,
    format,
    applyHighlight,
    getRowColor,
    clearHighlight,
    onClear,
    testIdBase = "selection",
    copyLabelKey = "bible.copy_verse",
    shareLabelKey = "bible.share_verse",
    copySuccessKey = "bible.verse_copied",
    shareTitleKey = "bible.share_title",
}) {
    const { t, lang } = useLang();

    if (selectedKeys.size === 0) return null;

    // When EVERY selected row already shares the same colour, the bar
    // highlights that swatch with a checkmark and a second tap will call
    // `clearHighlight` instead of `applyHighlight`.
    const colors = [...selectedKeys].map((k) => getRowColor?.(k) || null);
    const allSame = colors.length > 0 && colors.every((c) => c === colors[0]);
    const activeColor = allSame ? colors[0] : null;

    const doCopy = async () => {
        try {
            await navigator.clipboard.writeText(format(selectedKeys));
            toast.success(t(copySuccessKey));
        } catch {
            toast.error(t("common.error"));
        }
    };

    const doShare = async () => {
        const text = format(selectedKeys);
        if (navigator.share) {
            try {
                await navigator.share({ title: t(shareTitleKey), text });
                return;
            } catch { /* user cancelled */ }
        }
        await doCopy();
    };

    const onPickColor = async (colorId) => {
        try {
            if (activeColor === colorId) {
                // Second tap of the same colour → remove from favourites.
                const msg = await clearHighlight?.(selectedKeys);
                toast.success(msg || t("common.removed"));
            } else {
                const msg = await applyHighlight(selectedKeys, colorId);
                toast.success(msg || t("common.saved"));
            }
            onClear();
        } catch {
            toast.error(t("common.error"));
        }
    };

    const countLabel = lang === "es"
        ? `${selectedKeys.size} ${selectedKeys.size === 1 ? "seleccionado" : "seleccionados"}`
        : `${selectedKeys.size} selected`;

    return (
        <div
            role="toolbar"
            aria-label={countLabel}
            data-testid={`${testIdBase}-bar`}
            // Floating bar docked above the mobile bottom-nav. The
            // `--bottom-nav-h` CSS variable (see index.css) resolves to
            // the actual nav height + iOS PWA safe-area inset on mobile,
            // and to `0` on lg+ screens where the nav is hidden. Adding
            // 12 px on top keeps a visible air gap between the floating
            // bar and the nav. Lateral margin of 8 px so the bar
            // doesn't kiss the viewport edges. Rounded top corners only.
            className="fixed left-2 right-2 z-40 bg-stone900 text-sand-50 shadow-2xl border-t border-x border-white/10 rounded-t-xl"
            style={{ bottom: "calc(var(--bottom-nav-h) + 12px)" }}
        >
            <div className="max-w-2xl mx-auto flex flex-col gap-2 px-3 py-2.5 ui-sans">
                {/* Row 1 — Count + close on the left, Copy/Share on the right.
                    Hit targets are min 40×40 px so the bar is comfortably
                    tappable with a thumb. Icons enlarged to 28 px. */}
                <div
                    className="flex items-center justify-between gap-3"
                    data-testid={`${testIdBase}-actions-row`}
                >
                    <div className="flex items-center gap-1.5 min-w-0">
                        <button
                            type="button"
                            onClick={onClear}
                            aria-label={lang === "es" ? "Cancelar selección" : "Clear selection"}
                            data-testid={`${testIdBase}-clear`}
                            className="inline-flex items-center justify-center w-9 h-9 rounded-full text-sand-50/80 hover:text-sand-50 hover:bg-white/10 active:scale-95 transition-all shrink-0"
                        >
                            <X size={18} weight="bold" />
                        </button>
                        <span
                            className="font-semibold tabular-nums text-sm truncate"
                            data-testid={`${testIdBase}-count`}
                        >
                            {countLabel}
                        </span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                        <button
                            type="button"
                            onClick={doCopy}
                            aria-label={t(copyLabelKey)}
                            title={t(copyLabelKey)}
                            data-testid={`${testIdBase}-copy`}
                            className="inline-flex items-center justify-center w-11 h-11 rounded-lg hover:bg-white/10 active:bg-white/15 active:scale-95 transition-all"
                        >
                            <Copy size={28} weight="duotone" />
                        </button>
                        <button
                            type="button"
                            onClick={doShare}
                            aria-label={t(shareLabelKey)}
                            title={t(shareLabelKey)}
                            data-testid={`${testIdBase}-share`}
                            className="inline-flex items-center justify-center w-11 h-11 rounded-lg hover:bg-white/10 active:bg-white/15 active:scale-95 transition-all"
                        >
                            <ShareNetwork size={28} weight="duotone" />
                        </button>
                    </div>
                </div>

                {/* Row 2 — Five pastel swatches enlarged to 36 px so the
                    finger never lands on two colours at once; `gap-4`
                    plus `justify-around` keeps them comfortably spaced
                    across the full bar width. */}
                <div
                    className="flex items-center justify-around gap-4 px-1"
                    data-testid={`${testIdBase}-color-row`}
                    role="group"
                    aria-label={lang === "es" ? "Color del resaltado" : "Highlight colour"}
                >
                    {HIGHLIGHT_COLORS.map((c) => {
                        const isActive = activeColor === c.id;
                        return (
                            <button
                                key={c.id}
                                type="button"
                                onClick={() => onPickColor(c.id)}
                                aria-label={lang === "es" ? c.ariaEs : c.ariaEn}
                                aria-pressed={isActive}
                                data-testid={`${testIdBase}-color-${c.id}`}
                                className={[
                                    "relative w-9 h-9 rounded-full transition-transform",
                                    "ring-1 ring-white/30 active:scale-95",
                                    isActive ? "scale-110 ring-2 ring-white" : "hover:scale-105",
                                ].join(" ")}
                                style={{ backgroundColor: c.dot }}
                            >
                                {isActive && (
                                    <Check
                                        size={16}
                                        weight="bold"
                                        className="absolute inset-0 m-auto text-stone900"
                                        aria-hidden="true"
                                    />
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
